// Per-user server-persisted settings (dbo.v5_user_settings via
// GET/PUT /v5/users/me/settings). The store is generic key/value on the server;
// this hook exposes the typed snapshot { theme, sound, poemLanguages } and a
// write that sends a partial changes map.
//
// theme + sound model (the "set light → refresh → dark" bug fix): localStorage
// is the WORKING COPY (lib/theme.ts, lib/sound.ts) — the toggles write it
// directly, so a choice is instant AND survives refresh. The server is only a
// cross-device SEED: seedPreferencesFromServer() runs ONCE on the login
// transition (useLogin.onSuccess) to pull the server value into localStorage; a
// plain refresh never reads the server into localStorage, so it can't overwrite
// the user's choice. useUserPreferenceSync (in the authed layout) is
// write-through only: a same-tab toggle → PUT, nothing more.
//
// poemLanguages is the user's preferred work CONTENT-language codes (numeric,
// 1..10 — the WORK_LANGUAGE set); it pre-fills the works language filter. It's
// read straight from the server snapshot (useUserSettings) — no localStorage.

import { useEffect } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../client';
import type { components } from '../schema';
import {
  applyStoredTheme, getStoredTheme, type ThemeChoice,
} from '../../lib/theme';
import { applyStoredSound, getStoredSound } from '../../lib/sound';

export type UserSettings = components['schemas']['UserSettingsEndpointsUserSettingsResponse'];

const KEY = ['user-settings'] as const;

export function useUserSettings() {
  return useQuery({
    queryKey: KEY,
    queryFn: async ({ signal }) => {
      const { data, error } = await api.GET('/v5/users/me/settings', { signal });
      if (error) throw error;
      return data;
    },
    // Settings rarely change out from under us; a long stale time avoids refetch
    // churn. Writes update the cache directly via setQueryData below.
    staleTime: 5 * 60_000,
  });
}

/**
 * Write a partial set of changes (e.g. { 'ui.theme': 'dark' }). The server
 * validates all-or-nothing and returns the fresh snapshot, which we push into
 * the cache so every reader updates without a refetch.
 */
export function useUpdateUserSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (changes: Record<string, string>) => {
      const { data, error } = await api.PUT('/v5/users/me/settings', { body: { changes } });
      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      if (data) qc.setQueryData(KEY, data);
    },
  });
}

const THEME_EVENT = 'haket.theme-changed';
const SOUND_EVENT = 'haket.sound-changed';

/**
 * Bidirectional sync for the localStorage-fast-path preferences (theme, sound),
 * mounted ONCE in the authed layout:
 *
 *  1. HYDRATE (server → local): on the first successful settings load, push the
 *     server's saved theme/sound into localStorage (applyStored* no-ops if they
 *     already match, so a same-device user sees no flicker). The server is the
 *     source of truth across devices.
 *  2. WRITE-THROUGH (local → server): after hydration, any same-tab change to
 *     theme/sound (the header toggles / preferences) is PUT back to the server.
 *
 * The hydration write itself can't loop into a write-through: we only arm the
 * write-through AFTER hydration, and the hydrating applyStored* is a no-op when
 * the value is already current.
 */
export function useUserPreferenceSync(): void {
  const update = useUpdateUserSettings();

  // WRITE-THROUGH ONLY. localStorage is the working copy: the toggles
  // (useTheme/useSoundEnabled) write it directly for an instant, persistent-
  // across-refresh result. Here we just mirror a same-tab change up to the
  // server so it follows the user to other devices. We do NOT hydrate from the
  // server on mount — that's done ONCE on login (see seedPreferencesFromServer),
  // so a refresh can never overwrite what's in localStorage. Fire-and-forget;
  // a failed PUT just means the cross-device copy lags until the next change.
  useEffect(() => {
    const onThemeChange = () => update.mutate({ 'ui.theme': getStoredTheme() });
    const onSoundChange = () => update.mutate({ 'ui.sound': getStoredSound() ? 'true' : 'false' });
    window.addEventListener(THEME_EVENT, onThemeChange);
    window.addEventListener(SOUND_EVENT, onSoundChange);
    return () => {
      window.removeEventListener(THEME_EVENT, onThemeChange);
      window.removeEventListener(SOUND_EVENT, onSoundChange);
    };
  }, [update]);
}

/**
 * Seed the localStorage fast-path from the server's saved preferences — call
 * ONCE on the login transition (useLogin.onSuccess). After this, localStorage is
 * the working copy and is never overwritten by a server read, so a refresh keeps
 * whatever the user last chose. Best-effort: a failed fetch just leaves the
 * existing local values (or the shipped defaults) in place.
 */
export async function seedPreferencesFromServer(): Promise<void> {
  try {
    const { data, error } = await api.GET('/v5/users/me/settings');
    if (error || !data) return;
    applyStoredTheme(data.theme as ThemeChoice);
    applyStoredSound(data.sound);
  } catch {
    /* best-effort seed; ignore */
  }
}
